import React, { useState, useEffect } from 'react';
import { getAlerts, markAlertRead, simulateWebMonitoring, getOrganizations } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Bell, 
  Search,
  AlertTriangle,
  Info,
  Globe,
  RefreshCw,
  Check,
  ExternalLink
} from 'lucide-react';

const SEVERITY_CONFIG = {
  critical: { icon: AlertTriangle, color: 'bg-[#FF3B30]', textColor: 'text-[#FF3B30]' },
  high: { icon: AlertTriangle, color: 'bg-[#FF9500]', textColor: 'text-[#FF9500]' },
  medium: { icon: Info, color: 'bg-[#FFCC00]', textColor: 'text-[#FFCC00]' },
  low: { icon: Info, color: 'bg-[#34C759]', textColor: 'text-[#34C759]' },
  info: { icon: Info, color: 'bg-primary', textColor: 'text-primary' },
};

const ALERT_TYPE_CONFIG = {
  web_monitoring: { icon: Globe, label: 'Web Monitoring' },
  incident: { icon: AlertTriangle, label: 'Incident' },
  background_check: { icon: Search, label: 'Background Check' },
  system: { icon: Bell, label: 'System' },
};

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [alertsRes, orgsRes] = await Promise.all([
        getAlerts(),
        getOrganizations(),
      ]);
      setAlerts(alertsRes.data);
      setOrganizations(orgsRes.data);
      if (orgsRes.data.length > 0 && !selectedOrgId) {
        setSelectedOrgId(orgsRes.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (alertId) => {
    try {
      await markAlertRead(alertId);
      setAlerts(prev => prev.map(a => 
        a.id === alertId ? { ...a, is_read: true } : a
      ));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleSimulateMonitoring = async () => {
    if (!selectedOrgId) {
      toast.error('Please select an organization');
      return;
    }

    setSimulating(true);
    try {
      await simulateWebMonitoring(selectedOrgId);
      toast.success('Web monitoring simulation complete');
      fetchData();
    } catch (error) {
      toast.error('Failed to run simulation');
    } finally {
      setSimulating(false);
    }
  };

  const unreadCount = alerts.filter(a => !a.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="alerts-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            Alerts & Notifications
          </h1>
          <p className="text-muted-foreground mt-1">
            {unreadCount} unread alert{unreadCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Web Monitoring Panel */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            Web Monitoring
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
            <div className="flex-1 space-y-2">
              <p className="text-sm text-muted-foreground">
                Monitor web for arrests, lawsuits, misconduct allegations, and adverse media related to your organization.
                This system scans news sources within a 10-mile radius of your organization.
              </p>
              <div className="flex items-center gap-3">
                <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                  <SelectTrigger className="w-[250px]">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleSimulateMonitoring}
                  disabled={simulating}
                  className="btn-primary-glow"
                  data-testid="simulate-monitoring-btn"
                >
                  {simulating ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Run Scan
                </Button>
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-4 rounded-md bg-secondary/30 border border-border">
            <h4 className="font-semibold text-sm mb-2">Monitoring Coverage</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#FF3B30]" />
                <span className="text-muted-foreground">Arrests</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#FF9500]" />
                <span className="text-muted-foreground">Lawsuits</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#FFCC00]" />
                <span className="text-muted-foreground">Misconduct Allegations</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="text-muted-foreground">Adverse Media</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
            All Alerts
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {alerts.length > 0 ? (
            <div className="divide-y divide-border">
              {alerts.map((alert) => {
                const severityConfig = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
                const typeConfig = ALERT_TYPE_CONFIG[alert.alert_type] || ALERT_TYPE_CONFIG.system;
                const SeverityIcon = severityConfig.icon;
                const TypeIcon = typeConfig.icon;

                return (
                  <div
                    key={alert.id}
                    className={`p-4 flex items-start gap-4 transition-colors ${
                      !alert.is_read ? 'bg-secondary/20' : ''
                    }`}
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${severityConfig.color}/10`}>
                      <SeverityIcon className={`w-5 h-5 ${severityConfig.textColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium">{alert.title}</h4>
                        {!alert.is_read && (
                          <span className="w-2 h-2 rounded-full bg-primary" />
                        )}
                        <Badge variant="outline" className="text-xs">
                          <TypeIcon className="w-3 h-3 mr-1" />
                          {typeConfig.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {alert.description}
                      </p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-muted-foreground">
                          {new Date(alert.created_at).toLocaleString()}
                        </span>
                        {alert.source && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            {alert.source}
                          </span>
                        )}
                      </div>
                    </div>
                    {!alert.is_read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkRead(alert.id)}
                        data-testid={`mark-read-${alert.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Bell className="w-12 h-12 mb-4 opacity-50" />
              <p>No alerts</p>
              <p className="text-sm mt-1">Run a web monitoring scan to check for alerts</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
